import TopicalRiskProviderChart from '@app/src/pages/TopicalRisk/CompanyPage/TopicalRiskProviderChart'
import TopicalRiskProviderTimeline from '@app/src/pages/TopicalRisk/CompanyPage/TopicalRiskProviderTimeline'
import type { SustainabilityTopicProviderRisk } from '@app/src/types/topicalRisk'
import { processRiskTimeline } from '@app/src/utils/providerRiskDiffUtils'
import { Box, Grid, Paper, Skeleton } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { CompanyPageSection } from './CompanyPageSection'

interface RiskOverTimeSectionProps {
  providerRiskHistory: SustainabilityTopicProviderRisk[]
  isLoading: boolean
}

export function RiskOverTimeSection({ providerRiskHistory, isLoading }: RiskOverTimeSectionProps): JSX.Element {
  const { formatMessage } = useIntl()

  // Process risk history into timeline and chart data (pure function - testable without React)
  const { timelineItems, chartItems } = useMemo(() => processRiskTimeline(providerRiskHistory), [providerRiskHistory])

  return (
    <CompanyPageSection title={formatMessage({ id: 'topicalRisk.riskOverTime' })}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <Grid container spacing={2} alignItems="stretch" sx={{ pt: '0 !important' }}>
          <Grid item xs={12} lg={7}>
            <Paper variant="outlined" sx={{ px: 3, pt: 1, pb: 2, borderRadius: '6px', borderColor: 'grey.200' }}>
              <TopicalRiskProviderChart items={chartItems} />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Box sx={{ mt: { xs: 0, lg: -1.2 }, ml: { xs: 0, lg: 1 } }}>
              <TopicalRiskProviderTimeline items={timelineItems} />
            </Box>
          </Grid>
        </Grid>
      )}
    </CompanyPageSection>
  )
}

function LoadingSkeleton(): JSX.Element {
  return (
    <Grid container spacing={2} alignItems="stretch" sx={{ pt: 0 }}>
      <Grid item xs={12} md={7}>
        <Skeleton variant="rounded" height={400} animation="wave" />
      </Grid>
      <Grid item xs={12} md={5}>
        <Box sx={{ minHeight: 400, px: 2, py: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Skeleton variant="circular" width={10} height={10} animation="wave" sx={{ mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="40%" height={14} animation="wave" />
                  <Skeleton variant="text" width="60%" height={18} animation="wave" />
                  <Skeleton variant="text" width="90%" height={14} animation="wave" />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}
